/**
 * Inoreader: Read-later and RSS extension
 *
 * Declares a single web-accessible-resource group — `toast.html` plus every file under
 * `images/` — matched to `<all_urls>` with no `use_dynamic_url`. So the bundled UI images
 * and the toast page are all fetchable from any page at a fixed URL.
 *
 * The vendor-named logo images are the cleanest signal: they load through `probeImage`
 * with no CORS grant, so they survive a restrictive page CSP.
 *
 * Manifest verified against version 6.0.11.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'kfimphpokifbjgmjflanmfeppcjimgah';

export const inoreader: ExtensionDefinition = {
  id: ID,
  name: 'Inoreader: Read-later and RSS extension',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 50_000,
  notes:
    'toast.html and all images/ exposed to every origin with no dynamic URL. The vendor-named ' +
    'ino-logo images are conclusive and load without a CORS grant.',
  signals: [
    {
      id: 'war-ino-logo-image',
      kind: 'web-accessible-resource',
      description:
        'Load images/ino-logo-text-blue.png via <img>. A vendor-named logo exposed to every ' +
        'origin; needs no CORS grant, so it works even under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'images/ino-logo-text-blue.png'),
    },
    {
      id: 'war-toast-html',
      kind: 'web-accessible-resource',
      description: 'Fetch toast.html — the notification page exposed alongside the images.',
      weight: 0.5,
      run: () => probeFetch(ID, 'toast.html'),
    },
    {
      id: 'war-ino-logo-svg',
      kind: 'web-accessible-resource',
      description:
        'Load images/ino_logo.svg via <img> — a second vendor-named logo, independent of ' +
        'the png and the html paths.',
      weight: 0.5,
      run: () => probeImage(ID, 'images/ino_logo.svg'),
    },
  ],
};
